// frontend/src/hooks/useCachedApi.js
import { useState, useEffect, useRef } from 'react';

const apiCache = new Map();
const cacheTimestamps = new Map();
const pendingRequests = new Map();

export const useCachedApi = (apiCall, dependencies = [], options = {}) => {
  const { cacheDuration = 60000, cacheKey = null } = options;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  
  const getCacheKey = () => {
    if (cacheKey) return cacheKey;
    return `${apiCall.toString()}-${JSON.stringify(dependencies)}`;
  };

  const abortControllerRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    const key = getCacheKey();

  const fetchData = async () => {
  const cachedData = apiCache.get(key);
  const cacheTime = cacheTimestamps.get(key);
  const now = Date.now();

  // ✅ 1. Cache valide → retour immédiat
  if (cachedData && cacheTime && (now - cacheTime) < cacheDuration) {
    setData(cachedData);
    setLoading(false);
    return;
  }

  // 🔥 2. Requête déjà en cours → on l’attend
  if (pendingRequests.has(key)) {
    try {
      setLoading(true);
      const result = await pendingRequests.get(key);
      setData(result);
    } finally {
      setLoading(false);
    }
    return;
  }

  // ✅ 3. Nouvelle requête
  const requestPromise = (async () => {
    const response = await apiCall();
    return response.data;
  })();

  pendingRequests.set(key, requestPromise);

  try {
    setLoading(true);
    setError(null);

    const result = await requestPromise;

    if (isMounted) {
      apiCache.set(key, result);
      cacheTimestamps.set(key, now);
      setData(result);
    }
  } catch (err) {
    if (isMounted && err.name !== 'AbortError') {
      setError(err);
    }
  } finally {
    pendingRequests.delete(key);
    if (isMounted) {
      setLoading(false);
    }
  }
};


    fetchData();

    return () => {
      isMounted = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, dependencies);

  const refresh = () => {
    const key = getCacheKey();
    apiCache.delete(key);
    cacheTimestamps.delete(key);
    setLoading(true);
  };

  return { data, loading, error, refresh };
};

export const clearApiCache = () => {
  apiCache.clear();
  cacheTimestamps.clear();
};